import CardState, { type CardStateValue } from '@/components/ui/CardState'
import SectionHead from '@/components/ui/SectionHead'
import SeeAllLink from '@/components/ui/SeeAllLink'
import RailNewsRow, { RailNewsRowSkeleton } from '@/features/market-rail/components/RailNewsRow'
import RailRows from '@/features/market-rail/components/RailRows'
import { useMarketUpdates } from '@/features/market-rail/api/queries'
import type { RailUpdate } from '@/features/market-rail/api/railSchemas'
import { useIsMarketRoute } from '@/features/market-rail/marketRoute'
import { useRailBlock } from '@/features/market-rail/useRailFit'
import { useManualRefresh } from '@/hooks/useManualRefresh'

/**
 * The MAXIMUM, not the count. Four is the live design's own (4236:69879) and
 * what this card draws whenever there is room for it; the rail's fit draws
 * fewer, down to one, on a window too short for the whole rail.
 *
 * The QUERY still asks for five, because five is what Market Overview asks
 * for and the endpoint cache keys on the argument list — see
 * `UPDATE_PAGE_SIZE` in railService.ts. Fetch what the app fetches, slice
 * here.
 */
const RAIL_ROWS = 4

/** The skeleton's rows, one per row the card can draw, trimmed by the same fit. */
const SKELETON_ROWS = Array.from({ length: RAIL_ROWS }, (_, index) => (
  <RailNewsRowSkeleton key={index} />
))

/**
 * The eyebrow, as the reference prints it: "Banks, 2 Bhadra 2083, about NABIL".
 * Each part is left out when the wire lacks it. `MarketUpdate` carries no
 * sector for a company note, so only a sector-kind note gets the leading
 * sector word; `about` names the company only for a note that opens one.
 */
function eyebrowParts(item: RailUpdate): Array<string | undefined> {
  return [
    item.sector,
    item.publishedOn === '—' ? undefined : item.publishedOn,
    item.stockSymbol === undefined ? undefined : `about ${item.stockSymbol}`,
  ]
}

/**
 * One Market-news row: an eyebrow above a wrapped headline, nothing else.
 * Exported so a family rail (Stock, Portfolio) can draw the same row
 * without re-deriving its markup; `MarketRail` is not the only caller this
 * anatomy is built for.
 *
 * A row about one listed company opens it; a sector or market-wide note has
 * no page of its own to open and renders as plain text — same rule the old
 * `stockSymbol`-gated `ListRow` used.
 */
export function MarketNewsRow({ item }: { item: RailUpdate }) {
  return (
    <RailNewsRow
      meta={eyebrowParts(item)}
      title={item.title}
      {...(item.stockSymbol === undefined
        ? {}
        : { link: { to: '/stocks/$symbol', params: { symbol: item.stockSymbol } } })}
    />
  )
}

/**
 * The rail's Market-news block: up to four plain-language notes on what the
 * session did — `RAIL_ROWS`, or as many of them as the rail's fit leaves
 * room for (`RailRows`), and none at all once the fit drops the block on a
 * very short window (`useRailBlock`). Flat, hairline-separated, a `SectionHead` + `SeeAllLink` in place
 * of the filled `SectionCard` this replaced (2026-09-28 design refresh,
 * `4236:69879`).
 *
 * ── Headline only, no summary ─────────────────────────────────────────────
 *
 * The headline is the reference's 14/400, wrapped; `line-clamp-3` is a safety
 * cap rather than a promise every row wraps that far.
 *
 * ── The trailing column is gone; the eyebrow is a DATE, and the design asks
 *    for a time ──────────────────────────────────────────────────────────
 *
 * The V2 render prints "2 Bhadra 2083" (a date) beside the category word, not
 * a clock time — matching the wire: `MarketUpdateSerializer.published_at` is
 * a DRF `DateField` (`Format: date`), so there is no time-of-day in the
 * response for any mapping to recover. Full reasoning and the backend ask
 * are on `railUpdateSchema`. ⚠️ BACKEND.
 */
export default function MarketUpdatesCard() {
  const market = useIsMarketRoute()
  const block = useRailBlock('news')
  const updates = useMarketUpdates()
  const { data, isPending, isError } = updates
  const manual = useManualRefresh([updates])

  const state: CardStateValue = isPending
    ? 'loading'
    : isError
      ? 'failed'
      : data.length === 0
        ? 'empty'
        : manual.isManual
          ? 'refreshing'
          : 'loaded'

  return (
    <section aria-label="Market news" {...block}>
      <SectionHead
        size="sm"
        title="Market news"
        action={
          // The Market rail's own link is the mock's "More" at 14; every other rail keeps the
          // 12px "See all". It moves to /market/news when `marketNewsService.available` flips true.
          market ? (
            <SeeAllLink to="/market/update" aria-label="More market updates">
              More
            </SeeAllLink>
          ) : (
            <SeeAllLink to="/market/update" size="rail" aria-label="See all market updates" />
          )
        }
      />
      {/* Rows and skeleton rows both draw through `RailRows`, so the fit
          trims a loading list exactly as it will the loaded one, and the
          rail does not lurch when the request settles. */}
      <div className="mt-3">
        <CardState
          state={state}
          skeleton={<RailRows block="news" rows={SKELETON_ROWS} />}
          onRetry={manual.refresh}
          failedTitle="Couldn't load the updates"
          failedDescription="It will be here when the market feed answers."
          emptyTitle="Nothing from the desk yet"
          emptyDescription="Notes appear here as the session gives them something to say."
        >
          {data === undefined ? null : (
            <RailRows
              block="news"
              rows={data.slice(0, RAIL_ROWS).map((item) => (
                <MarketNewsRow key={item.id} item={item} />
              ))}
            />
          )}
        </CardState>
      </div>
    </section>
  )
}
